import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import MainPanel from '../components/MainPanel';
import { getAssemblyMetadata } from '../services/apiService';
import './HomePage.css';

const HomePage = ({ user, isPropelAuthenticated }) => {
  const [propelStatus] = useState(isPropelAuthenticated || false);
  const [documentId, setDocumentId] = useState(null);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [loadingElement, setLoadingElement] = useState(false);
  const [elementError, setElementError] = useState(null);
  const [elementId, setElementId] = useState(null);
  const [elementType, setElementType] = useState(null); // ASSEMBLY, PARTSTUDIO, etc.

  useEffect(() => {
    let isLoading = false; // Prevent multiple simultaneous loads
    
    const loadOnshapeContext = async () => {
      if (isLoading) {
        console.log('[HomePage.js] Context load already in progress, skipping...');
        return;
      }
      isLoading = true;
      
      const params = new URLSearchParams(window.location.search);
      const docId = params.get('documentId');
      const wsId = params.get('workspaceId');
      const elemId = params.get('elementId');
      const tabId = params.get('tabId');
      const userId = params.get('userId');
      const companyId = params.get('companyId'); // Capture companyId for multi-tenant validation

      if (docId && wsId && elemId) {
        // Context is available in URL - use it and store it in session
        console.log('[HomePage.js] Using Onshape context from URL params');
        setDocumentId(docId);
        setWorkspaceId(wsId);
        setElementId(elemId);
        
        // Send context to server to store in session
        try {
          console.log('[HomePage.js] Sending context to server for session storage...');
          const response = await fetch('/api/onshape-context', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
              documentId: docId,
              workspaceId: wsId,
              elementId: elemId,
              tabId,
              userId,
              companyId // Include companyId for validation
            })
          });
          
          if (response.ok) {
            const result = await response.json();
            console.log('[HomePage.js] ✓ Context stored in session:', result);
          } else {
            console.warn('[HomePage.js] Failed to store context in session:', response.status);
          }
        } catch (error) {
          console.error('[HomePage.js] Error storing context in session:', error);
        }
      } else {
        // Context not in URL - try to fetch from session
        console.log('[HomePage.js] Context not in URL, fetching from session...');
        try {
          const response = await fetch('/api/onshape-context', {
            credentials: 'include'
          });
          if (response.ok) {
            const sessionContext = await response.json();
            if (sessionContext && sessionContext.documentId) {
              console.log('[HomePage.js] Loaded Onshape context from session:', sessionContext);
              setDocumentId(sessionContext.documentId);
              setWorkspaceId(sessionContext.workspaceId);
              setElementId(sessionContext.elementId);
              
              // Update URL with the restored context
              const newParams = new URLSearchParams(window.location.search);
              newParams.set('documentId', sessionContext.documentId);
              newParams.set('workspaceId', sessionContext.workspaceId);
              newParams.set('elementId', sessionContext.elementId);
              if (sessionContext.tabId) newParams.set('tabId', sessionContext.tabId);
              if (sessionContext.userId) newParams.set('userId', sessionContext.userId);
              const newUrl = `${window.location.pathname}?${newParams.toString()}`;
              window.history.replaceState({}, '', newUrl);
              console.log('[HomePage.js] Updated URL with restored context');
            } else {
              console.log('[HomePage.js] No context found in session');
            }
          }
        } catch (error) {
          console.error('[HomePage.js] Error fetching context from session:', error);
        }
      }
    };

    loadOnshapeContext();
  }, []);

  useEffect(() => {
    if (documentId && workspaceId && elementId) {
      setLoadingElement(true);
      setElementError(null);
      setSelectedElement(null);
      setElementType(null);
      getAssemblyMetadata(documentId, workspaceId, elementId)
        .then(data => {
          setSelectedElement(data);
          setElementType(data.elementType || null); // ASSEMBLY, PARTSTUDIO, etc.
          console.log('[HomePage.js] Element type:', data.elementType);
        })
        .catch(err => setElementError(err.message || 'Failed to load element metadata.'))
        .finally(() => setLoadingElement(false));
    } else {
      setSelectedElement(null);
      setElementType(null);
    }
  }, [documentId, workspaceId, elementId]);

  if (loadingElement) return (
    <div className="centered-message">
      Loading assembly info...
    </div>
  );
  
  if (elementError) return (
    <div className="centered-message error-message">
      Error: {elementError}
    </div>
  );

  return (
    <div className="home-page">
      <Header userName={user?.name} isPropelAuthenticated={propelStatus} documentId={documentId} workspaceId={workspaceId} />
      <MainPanel
        selectedElement={selectedElement}
        documentId={documentId}
        workspaceId={workspaceId}
        isPropelAuthenticated={propelStatus}
        user={user}
        elementType={elementType}
      />
    </div>
  );
};

export default HomePage;
